import React, {useEffect, useState} from 'react';
import {BrandSafetyPrompt} from '../types';
import {useAuth} from '../contexts/AuthContext';
import {useCompany} from '../contexts/CompanyContext';
import DeliveryFunnelDomainsModal from './DeliveryFunnelDomainsModal';
import type {DeliveryFunnelDomainRow} from '../types/deliveryFunnel';
import type {DeliveryFunnelOpenDomainsPayload} from './DeliveryFunnelSection';
import SearchFilterBar from './promptManager/SearchFilterBar';
import BlueprintsTable from './promptManager/BlueprintsTable';
import BlueprintFormModal from './promptManager/BlueprintFormModal';
import AIRewriteModal from './promptManager/AIRewriteModal';
import {usePromptList} from './promptManager/usePromptList';
import {useConnectedJobs} from './promptManager/useConnectedJobs';
import {useBlueprintForm} from './promptManager/useBlueprintForm';

const PromptManager: React.FC = () => {
    const {user} = useAuth();
    // Delivery Funnel is admin-only. user.role already reflects the impersonated
    // role when impersonating, so this matches the sidebar's admin gating.
    const isAdmin = user?.role === 'admin';
    const {selectedCompanyId} = useCompany();

    const [error, setError] = useState<string | null>(null);

    const list = usePromptList(user, selectedCompanyId, setError);
    const {connectedJobsFor, estScanCostFor} = useConnectedJobs(isAdmin);
    const form = useBlueprintForm(user, selectedCompanyId, list.companies, list.reload, setError);

    const [expandedSimulatorPromptId, setExpandedSimulatorPromptId] = useState<string | null>(null);
    const [expandedFunnelPromptId, setExpandedFunnelPromptId] = useState<string | null>(null);

    // Per-domain drill-down modal (bd be0.6). State lives in this parent so
    // closing the modal does not collapse the delivery-funnel section that
    // owns the trigger link.
    const [domainsModalOpen, setDomainsModalOpen] = useState(false);
    const [domainsModalDomains, setDomainsModalDomains] = useState<DeliveryFunnelDomainRow[]>([]);
    const [domainsModalDateRangeLabel, setDomainsModalDateRangeLabel] = useState('');
    const [domainsModalCampaignLabel, setDomainsModalCampaignLabel] = useState('');

    const openDomainsModal = ({domains, rangeDays, campaignId, campaignName}: DeliveryFunnelOpenDomainsPayload) => {
        setDomainsModalDomains(domains);
        setDomainsModalDateRangeLabel(`last ${rangeDays} days`);
        setDomainsModalCampaignLabel(
            campaignId === null
                ? 'all campaigns'
                : campaignName
                    ? `campaign ${campaignName}`
                    : `campaign ${campaignId}`,
        );
        setDomainsModalOpen(true);
    };

    const [strategySimulatorEnabled, setStrategySimulatorEnabled] = useState(false);
    useEffect(() => {
        const simulatorAllowedEmail = 'marko.dupor@eskimi.com';
        const isAllowedUser = (user?.email || '').toLowerCase() === simulatorAllowedEmail;
        setStrategySimulatorEnabled(isAllowedUser);
    }, [user?.email]);

    return (
        // Flex column so the table region can grow to fill the viewport
        // instead of leaving a gray gap below the rows.
        <div className="flex flex-col space-y-3 h-full min-h-0">
            <SearchFilterBar
                isolatedBlueprintId={list.isolatedBlueprintId}
                isolatedBlueprintName={list.isolatedBlueprintName}
                clearIsolation={list.clearIsolation}
                searchTerm={list.searchTerm}
                setSearchTerm={list.setSearchTerm}
                showOnlyMyBlueprints={list.showOnlyMyBlueprints}
                setShowOnlyMyBlueprints={list.setShowOnlyMyBlueprints}
                onCreate={form.handleCreate}
            />

            {error && (
                /* Risk-tinted band (page lock 7) — same anatomy as the
                   accent isolation banner, on the risk token pair. */
                <div
                    data-testid="blueprint-error-banner"
                    className="flex gap-3 rounded-xl border border-risk bg-risk-bg px-4 py-3"
                    role="alert"
                >
                    <svg className="h-4 w-4 flex-shrink-0 text-risk" viewBox="0 0 20 20" fill="currentColor">
                        <path fillRule="evenodd"
                              d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                              clipRule="evenodd"/>
                    </svg>
                    <div>
                        <p className="text-xs text-risk">{error}</p>
                        <button
                            onClick={() => setError(null)}
                            className="mt-1 text-xs font-bold text-risk underline hover:no-underline"
                        >
                            Dismiss
                        </button>
                    </div>
                </div>
            )}

            <BlueprintsTable
                prompts={list.prompts}
                isLoading={list.isLoading}
                isFullPage={list.isFullPage}
                searchTerm={list.searchTerm}
                showOnlyMyBlueprints={list.showOnlyMyBlueprints}
                isAdmin={isAdmin}
                user={user}
                companies={list.companies}
                connectedJobsFor={connectedJobsFor}
                estScanCostFor={estScanCostFor}
                strategySimulatorEnabled={strategySimulatorEnabled}
                expandedSimulatorPromptId={expandedSimulatorPromptId}
                setExpandedSimulatorPromptId={setExpandedSimulatorPromptId}
                expandedFunnelPromptId={expandedFunnelPromptId}
                setExpandedFunnelPromptId={setExpandedFunnelPromptId}
                onEdit={(prompt: BrandSafetyPrompt) => form.handleEdit(prompt)}
                onDelete={form.handleDelete}
                onOpenDomains={openDomainsModal}
                paginationInfo={list.paginationInfo}
                onPageChange={list.setCurrentPage}
            />

            <DeliveryFunnelDomainsModal
                open={domainsModalOpen}
                onClose={() => setDomainsModalOpen(false)}
                domains={domainsModalDomains}
                dateRangeLabel={domainsModalDateRangeLabel}
                campaignLabel={domainsModalCampaignLabel}
            />

            <BlueprintFormModal
                open={form.showCreateModal || !!form.editingPrompt}
                editingPrompt={form.editingPrompt}
                user={user}
                isLoadingModal={form.isLoadingModal}
                contentLoadError={form.contentLoadError}
                formError={form.formError}
                formData={form.formData}
                setFormData={form.setFormData}
                allCategoriesExcluded={form.allCategoriesExcluded}
                syncToIndex={form.syncToIndex}
                setSyncToIndex={form.setSyncToIndex}
                syncToEquativ={form.syncToEquativ}
                setSyncToEquativ={form.setSyncToEquativ}
                syncToEquativPublic={form.syncToEquativPublic}
                setSyncToEquativPublic={form.setSyncToEquativPublic}
                syncToPubmatic={form.syncToPubmatic}
                setSyncToPubmatic={form.setSyncToPubmatic}
                advertiserSearchTerm={form.advertiserSearchTerm}
                setAdvertiserSearchTerm={form.setAdvertiserSearchTerm}
                loadingAdvertisers={form.loadingAdvertisers}
                advertisers={form.advertisers}
                isRewriting={form.isRewriting}
                isLoadingMatchInsights={form.isLoadingMatchInsights}
                matchInsights={form.matchInsights}
                onGetMatchInsights={form.handleGetMatchInsights}
                onRewriteWithAI={form.handleRewriteWithAI}
                contentMode={form.contentMode}
                setContentMode={form.setContentMode}
                brandUrl={form.brandUrl}
                setBrandUrl={form.setBrandUrl}
                isGeneratingPrompt={form.isGeneratingPrompt}
                onGenerateWithAI={form.handleGenerateWithAI}
                onSave={form.handleSave}
                onClose={form.closeModal}
                onSelectAdvertiser={form.selectAdvertiser}
                onClearAdvertiser={form.clearAdvertiser}
            />

            <AIRewriteModal
                show={form.showAIRewrite}
                rewriteResult={form.rewriteResult}
                onAccept={form.handleAcceptAIRewrite}
                onReject={form.handleRejectAIRewrite}
            />
        </div>
    );
};

export default PromptManager;
